import type { EnqueueLeadUseCase } from '@/application/use-cases/EnqueueLeadUseCase';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { isFakeLeadInputValid } from '@/domain/entities/FakeLead';
import type { FakeLeadInput } from '@/domain/entities/FakeLead';
import { toWireSafeLeadCaptureInput } from '@/domain/entities/LeadCaptureInput';

export type CaptureFakeLeadResult =
  | { success: true; leadName: string | null; showQueueAlert: boolean }
  | { success: false; reason: 'invalid-input'; error: string }
  | { success: false; reason: 'queue-full' }
  | { success: false; reason: 'storage-unavailable' };

/**
 * Use case responsável por capturar um lead fictício no evento autenticado.
 * Valida os dados de entrada e delega a persistência a `EnqueueLeadUseCase`
 * (HUB-236) — desde a Fatia 2, a captura **não chama mais `ILeadRepository`
 * diretamente a partir da UI** (Spec Técnica > Arquitetura): o lead é
 * gravado imediatamente no IndexedDB (CA-01), e a sincronização de fato com
 * o servidor é responsabilidade assíncrona de `SyncLeadQueueUseCase`
 * (Background Sync ou polling fallback).
 *
 * `leadName: null` no resultado de sucesso representa a recusa de
 * consentimento (HUB-201): a ativação prossegue mesmo assim, sem dados
 * pessoais enviados (`toWireSafeLeadCaptureInput`, generalizado na Fatia 3 —
 * HUB-237, CA-20 — substitui nome/email por um placeholder não-pessoal
 * antes de enfileirar). Diferente da Fatia 1, essa
 * decisão agora é só local (o próprio `input.consentAccepted`) — não
 * depende mais de uma resposta HTTP 202 do servidor, porque não há mais
 * chamada de rede síncrona neste passo.
 *
 * Sessão de dispositivo expirada em uso (HTTP 410) deixou de ser um
 * resultado possível deste use case: como o enfileiramento é 100% local,
 * uma sessão que expira só é detectada mais tarde, durante a sincronização
 * em segundo plano (`SyncLeadQueueUseCase`) — cenário coberto pelos riscos
 * conhecidos da spec (Riscos técnicos, item 1), não por uma tela dedicada.
 */
export class CaptureFakeLeadUseCase {
  constructor(private readonly enqueueLeadUseCase: EnqueueLeadUseCase) {}

  async execute(
    session: DeviceSession,
    input: FakeLeadInput,
    consentTextVersion: number
  ): Promise<CaptureFakeLeadResult> {
    if (!isFakeLeadInputValid(input)) {
      return {
        success: false,
        reason: 'invalid-input',
        error:
          'Informe nome e email para continuar, ou recuse o consentimento para participar sem informar seus dados.',
      };
    }

    const wireInput = toWireSafeLeadCaptureInput(input);
    const result = await this.enqueueLeadUseCase.execute(session, wireInput, consentTextVersion);

    if (result.outcome === 'queue-full') {
      return { success: false, reason: 'queue-full' };
    }

    if (result.outcome === 'storage-unavailable') {
      return { success: false, reason: 'storage-unavailable' };
    }

    return {
      success: true,
      leadName: input.consentAccepted ? input.name : null,
      showQueueAlert: result.showQueueAlert,
    };
  }
}
